/**
 * The steps a store purchase runs through, written down once.
 *
 * Two paths in `iap.service.ts` spend a buyer's money at a store: `purchaseAsync`
 * for somebody buying personal Premium, and `purchaseCoachTierAsync` for a coach
 * buying a coaching tier. They ask different stores things in different ways, but
 * the ORDER of what they do, and every decision they make about the buyer's money,
 * is the same. This file holds that order and those decisions; each caller supplies
 * only the parts that genuinely differ.
 *
 * WHY THIS FILE EXISTS AT ALL. The skeleton used to be written out once per path.
 * When #4651 changed the rule about what the app may tell a buyer whose money has
 * already been taken, the change had to be made in each copy by hand — and it
 * reached two paths and missed five others, which went on telling buyers a purchase
 * had failed when the store simply could not be asked.
 * `design-docs/iap-subscriptions.md` records that miss. Nothing caught it: there was
 * no test anywhere near the file, and the copies did not reference each other. So
 * the point of this file is not tidiness. It is that the next change to how a
 * purchase outcome is judged is one edit instead of several, and that the edit has
 * a test beside it.
 *
 * THE ONE RULE IT HOLDS, WHICH IS THE ONE WORTH READING.
 * `chargedTheBuyer` below becomes true at exactly one point: the moment the store
 * hands us a purchase token. That is the point from which the buyer has paid.
 * Anything that fails after it is OUR confirmation step failing, not the purchase —
 * so the failure we return carries `confirmationFailedAfterPurchase: true`, and
 * nothing shown to that person may say their purchase failed. Anything that fails
 * BEFORE it leaves the flag false, and the terminal wording is correct there,
 * because no money moved. `buildStoreConfirmationFailure` in `iap.service.ts` makes
 * the same distinction one level down, for a server that answered but could not
 * check; this flag is for a store step that produced no answer at all.
 *
 * THE SAME POINT IS WHERE A PATH GETS TO WRITE THE PURCHASE DOWN.
 * `rememberUnconfirmedPurchase` is called there, and `onServerAnswered` once the
 * confirmation resolves. Between those two calls the buyer has paid and nobody but
 * this device knows it, so a path with no other way back — the coach-tier one — can
 * keep a record and re-send it later. Both are required members: a path that needs
 * no record says so with an empty body, rather than inheriting nothing by accident.
 *
 * WHY IT IS A PLAIN FUNCTION AND NOT A METHOD ON THE SERVICE. Written this way the
 * file imports neither Angular nor `@capacitor/core`, so `store-purchase-flow.test.ts`
 * can run it under mocha with plain fakes. That is the same shape the subscribe page
 * already uses next door for its own decisions (`post-purchase-wait-policy.ts`,
 * `purchase-outcome.ts`, `subscribe-restore-error-filter.ts`, each with its own
 * `.test.ts`), and the reason `online-state.service.ts` is testable.
 *
 * WHAT IS DELIBERATELY NOT HERE. `restorePurchasesAsync` does not use this flow and
 * must not be folded into it. It fetches a LIST of past purchases rather than making
 * one, has no cancel and no already-owned handling because that store call reports
 * neither, fetches the account id AFTER the store call instead of before, and its iOS
 * arm carries a deliberate `purchaseToken`/`orderId` swap whose own comment warns that
 * dropping the swap lets the same Apple subscription be linked to several users. It
 * shares only the tail, and folding it in would need enough extra parameters to lose
 * the point.
 */

/**
 * Which step of a purchase was running when something threw.
 *
 * Handed to `reportThrew` so the Sentry report says where the purchase broke
 * (#6244). Taken from where the flow actually was rather than from the error's
 * text, which differs between platforms and between store plugins. The values
 * leave the app as a Sentry tag, so they are lowercase words people type into
 * searches, and must stay few.
 */
export enum StorePurchaseStep {
  /** fetching the hashed account id the store purchase is tagged with */
  AccountId = 'account_id',
  /** the native store call itself: no money has moved yet */
  StoreCall = 'store_call',
  /** asking our server to confirm a purchase the store has charged for */
  Confirm = 'confirm',
  /** the re-login after a confirmed purchase */
  Finish = 'finish',
}

/** What a store call answers with. Mirrors the fields `BillingPlugin.purchase` and
 *  `BillingPlugin.updateSubscription` return, structurally rather than by import, so
 *  this file stays free of `@capacitor/core`. */
export interface StorePurchaseResponse {
  purchaseToken?: string;
  orderId?: string;
  productId?: string;
  canceled?: boolean;
  alreadyOwned?: boolean;
  staleTransaction?: boolean;
  staleReason?: string;
  /** iOS only: the store completed and charged, but the device could not verify
   *  Apple's signature on the receipt. The ids are still real enough for our
   *  server to look up with Apple, which is what actually grants anything. */
  unverifiedOnDevice?: boolean;
  unverifiedReason?: string;
  /** iOS only: StoreKit's signed copy of the transaction. Forwarded to
   *  `appleValidate` as proof this device holds the purchase, which lets an
   *  `already_linked` answer show the full email (#5871). */
  signedTransaction?: string;
}

/** A store response the flow has already checked for a purchase token, so callers
 *  past that guard do not have to re-handle a missing one. Only `validateAsync` sees
 *  this type, and it is the only place the token is guaranteed to exist. */
export interface ConfirmedStorePurchaseResponse extends StorePurchaseResponse {
  purchaseToken: string;
}

/** The shape every one of our store-validate endpoints answers with. Only `error`
 *  decides the flow; the rest travels through to the caller's failure builder. */
export interface StoreValidationResponse {
  error?: string;
}

/** What one purchase path answers with. Deliberately structural rather than imported
 *  from `iap.service.ts`: the coach-tier and personal paths return their own result
 *  types, and both are assignable to this. */
export interface StorePurchaseOutcome {
  success: boolean;
  canceled?: boolean;
  error?: string;
  confirmationFailedAfterPurchase?: boolean;
}

/**
 * The parts that differ between one purchase path and another.
 *
 * The three `report*` members take a callback rather than an event name on purpose.
 * Each caller reports under its own Sentry name with its own context object, and
 * passing callbacks keeps every one of those names a plain string literal at its call
 * site in `iap.service.ts`, where somebody grepping Sentry for a name can still find
 * the code that raises it.
 */
export interface StorePurchaseFlow<TValidation extends StoreValidationResponse> {
  /** false when in-app purchase does not exist on this platform (web, mostly). */
  isAvailable(): boolean;

  /** the hashed user id the stores use to tie a purchase to an account. Fetched
   *  before the store call because both stores want it as an input. */
  getAccountIdAsync(): Promise<string | undefined>;

  /** ask the store to take the money. */
  runStoreCallAsync(accountId: string | undefined): Promise<StorePurchaseResponse>;

  /** the store short-circuited to a lingering expired entitlement instead of making a
   *  fresh purchase. Reported, then treated as an ordinary cancel. */
  reportStaleTransaction(response: StorePurchaseResponse): void;

  /** the store charged, but the device could not verify the receipt. Reported, then
   *  confirmed with our server exactly like any other purchase. */
  reportUnverifiedOnDevice(response: StorePurchaseResponse): void;

  /** the store says this buyer already owns the product. What that means differs per
   *  path, so the answer is the caller's. */
  onAlreadyOwnedAsync(): Promise<StorePurchaseOutcome>;

  /** the store has just taken the buyer's money, and nobody else knows it yet.
   *
   *  Called at exactly one point — the moment the purchase token arrives, before
   *  our server is told anything — so that a path which has no other way of
   *  recovering can write the purchase down on the device and re-send it later.
   *  Nothing here may throw: a failure to remember must not stop the confirmation
   *  that follows, which is the step that actually grants what was bought.
   *
   *  Required rather than optional, so the next purchase path added has to write
   *  its own answer instead of silently inheriting nothing. A path with a
   *  recovery of its own says so with an empty body and a comment. */
  rememberUnconfirmedPurchase(
    response: ConfirmedStorePurchaseResponse,
    accountId: string | undefined,
  ): void;

  /** our server has answered — granted, refused, or said it could not check.
   *
   *  Called once `validateAsync` RESOLVES, whatever it resolved with, and never
   *  when it throws: a throw is the case where nobody knows about the purchase,
   *  which is the case the record above exists for. Which answers are worth
   *  keeping the record for is the caller's decision, not this file's. Nothing
   *  here may throw, for the same reason as above. */
  onServerAnswered(validation: TValidation): void;

  /** ask our server to confirm the completed purchase and grant what was bought.
   *  Called only past the purchase-token guard, which is why the response it gets
   *  is the narrowed one. */
  validateAsync(
    response: ConfirmedStorePurchaseResponse,
    accountId: string | undefined,
  ): Promise<TValidation>;

  /** our server answered, and refused. */
  reportValidateFailed(validation: TValidation): void;

  /** turn that refusal into this path's own failure answer — in practice
   *  `buildStoreConfirmationFailure`, which decides whether the server settled the
   *  question or merely could not check. */
  buildValidationFailure(validation: TValidation): StorePurchaseOutcome;

  /** something threw and we got no answer about the purchase from anywhere.
   *  `step` says which part of the purchase was running when it did. */
  reportThrew(error: unknown, step: StorePurchaseStep): void;

  /** turn that throw into text for the failure answer. */
  describeThrown(error: unknown): string;

  /** re-login so the app picks up what was just granted. */
  finishAsync(): Promise<unknown>;
}

/** The refusal when the platform has no in-app purchase at all. Shared so both paths
 *  cannot drift apart on the wording. */
export const IAP_UNAVAILABLE_ERROR = 'IAP not available on this platform.';

/** The refusal when the store completed without handing back a token. This is BEFORE
 *  the buyer is charged as far as we can tell, so the failure is a terminal one and
 *  carries no `confirmationFailedAfterPurchase`. The iOS plugin hands a token back even
 *  when the device could not verify the receipt (`unverifiedOnDevice`), so a charged
 *  purchase does not end up here for that reason. */
export const NO_PURCHASE_TOKEN_ERROR = 'No purchase token returned.';

/**
 * Run one store purchase and answer for it.
 *
 * Never throws: every failure comes back as a `StorePurchaseOutcome`, because the
 * callers' job is to show the buyer something, not to propagate.
 */
export async function runStorePurchaseAsync<TValidation extends StoreValidationResponse>(
  flow: StorePurchaseFlow<TValidation>,
): Promise<StorePurchaseOutcome> {
  if (!flow.isAvailable()) {
    return { success: false, error: IAP_UNAVAILABLE_ERROR };
  }

  // becomes true the moment the store hands us a purchase token, which is the point
  // from which the buyer has been charged. Everything that fails after it is OUR step
  // failing, not the purchase (#4651).
  let chargedTheBuyer = false;
  // which step is running, so a throw can be reported with where it happened (#6244)
  let step = StorePurchaseStep.AccountId;

  try {
    const accountId = await flow.getAccountIdAsync();
    step = StorePurchaseStep.StoreCall;
    const response = await flow.runStoreCallAsync(accountId);

    if (response.canceled) {
      // staleTransaction means the store short-circuited to a lingering
      // expired/revoked entitlement instead of creating a fresh purchase (#3470).
      // Report it, then treat it as the silent cancel it looks like to the buyer.
      if (response.staleTransaction) {
        flow.reportStaleTransaction(response);
      }
      return { success: false, canceled: true };
    }

    if (response.alreadyOwned) {
      return await flow.onAlreadyOwnedAsync();
    }

    const purchaseToken = response.purchaseToken;
    if (!purchaseToken) {
      return { success: false, error: NO_PURCHASE_TOKEN_ERROR };
    }

    // the store has completed the purchase and taken the money from here on
    chargedTheBuyer = true;

    // the device could not check the receipt's signature. Not a failure: our
    // server confirms the purchase with the store itself (df-e8d8826c8191).
    if (response.unverifiedOnDevice) {
      flow.reportUnverifiedOnDevice(response);
    }

    // write it down before asking our server anything. If the call below throws,
    // this record is the only thing that knows the buyer paid (`df-2745c2d64a48`).
    const confirmedResponse = { ...response, purchaseToken };
    flow.rememberUnconfirmedPurchase(confirmedResponse, accountId);

    step = StorePurchaseStep.Confirm;
    const validation = await flow.validateAsync(confirmedResponse, accountId);
    // our server answered — the record above has done its job either way.
    flow.onServerAnswered(validation);
    if (validation.error) {
      flow.reportValidateFailed(validation);
      return flow.buildValidationFailure(validation);
    }

    step = StorePurchaseStep.Finish;
    await flow.finishAsync();

    return { success: true };
  } catch (error) {
    flow.reportThrew(error, step);
    // a throw once the store has charged the buyer means we never got any answer
    // about the purchase at all — a missing sign-in token and a dropped network both
    // land here. That is the same "we could not check" situation the server reports
    // with its own code, so it gets the same honest message (#4651).
    return {
      success: false,
      error: flow.describeThrown(error),
      confirmationFailedAfterPurchase: chargedTheBuyer,
    };
  }
}
